export type ScratchpadSaveResult = {
  data: unknown;
};

export type PendingScratchpadSave = {
  notes: string;
  settled: Promise<void>;
};

type ScratchpadSaveStateOptions = {
  retentionMs?: number;
};

type LatestScratchpadSave = {
  generation: number;
};

type RetainedScratchpadSave = PendingScratchpadSave & {
  generation: number;
  expiresAt: number;
  expiryTimer: ReturnType<typeof setTimeout>;
};

const DEFAULT_RETENTION_MS = 30_000;
const DEFAULT_SCOPE = "component";

function stateKey(scope: string, id: number): string {
  return JSON.stringify([scope, id]);
}

/**
 * Holds only short-lived, in-memory save coordination state.
 */
export function createScratchpadSaveState(
  options: ScratchpadSaveStateOptions = {},
) {
  const retentionMs = options.retentionMs ?? DEFAULT_RETENTION_MS;
  const latestByKey = new Map<string, LatestScratchpadSave>();
  const pendingByKey = new Map<string, RetainedScratchpadSave>();
  const chainByKey = new Map<string, Promise<void>>();
  let activeScope: string | undefined;
  let generation = 0;

  const clearRetainedState = () => {
    for (const pending of pendingByKey.values()) {
      clearTimeout(pending.expiryTimer);
    }
    latestByKey.clear();
    pendingByKey.clear();
    chainByKey.clear();
  };

  const activateScope = (scope: string) => {
    if (activeScope === scope) return;
    clearRetainedState();
    activeScope = scope;
  };

  const clearScope = (scope: string) => {
    if (activeScope !== scope) return;
    clearRetainedState();
    activeScope = undefined;
  };

  const isActive = (scope: string) => activeScope === scope;

  const begin = (scope: string, id: number) => {
    if (!isActive(scope)) return undefined;
    const key = stateKey(scope, id);
    const previousPending = pendingByKey.get(key);
    if (previousPending) clearTimeout(previousPending.expiryTimer);

    generation += 1;
    const saveGeneration = generation;
    latestByKey.set(key, { generation: saveGeneration });
    pendingByKey.delete(key);
    return saveGeneration;
  };

  const isCurrent = (scope: string, id: number, saveGeneration: number) => {
    if (!isActive(scope)) return false;
    const latest = latestByKey.get(stateKey(scope, id));
    return latest?.generation === saveGeneration;
  };

  const complete = (scope: string, id: number, saveGeneration: number) => {
    if (!isCurrent(scope, id, saveGeneration)) return false;
    const key = stateKey(scope, id);
    const pending = pendingByKey.get(key);
    if (pending) clearTimeout(pending.expiryTimer);
    latestByKey.delete(key);
    pendingByKey.delete(key);
    return true;
  };

  const setPending = (
    scope: string,
    id: number,
    saveGeneration: number,
    pending: PendingScratchpadSave,
  ) => {
    if (!isCurrent(scope, id, saveGeneration)) return;
    const key = stateKey(scope, id);
    let retained!: RetainedScratchpadSave;
    retained = {
      ...pending,
      generation: saveGeneration,
      expiresAt: Date.now() + retentionMs,
      expiryTimer: setTimeout(() => {
        if (pendingByKey.get(key) !== retained) return;
        pendingByKey.delete(key);
        if (latestByKey.get(key)?.generation === saveGeneration) {
          latestByKey.delete(key);
        }
      }, retentionMs),
    };
    pendingByKey.set(key, retained);
  };

  const getPending = (scope: string, id: number) => {
    const key = stateKey(scope, id);
    const pending = pendingByKey.get(key);
    if (!isActive(scope) || pending === undefined) {
      return undefined;
    }
    if (pending.expiresAt <= Date.now()) {
      clearTimeout(pending.expiryTimer);
      pendingByKey.delete(key);
      if (latestByKey.get(key)?.generation === pending.generation) {
        latestByKey.delete(key);
      }
      return undefined;
    }
    return pending;
  };

  const consumePending = (
    scope: string,
    id: number,
    pending: PendingScratchpadSave,
  ) => {
    const key = stateKey(scope, id);
    if (getPending(scope, id) !== pending) return false;
    const retained = pendingByKey.get(key);
    if (retained) clearTimeout(retained.expiryTimer);
    pendingByKey.delete(key);
    return true;
  };

  const hasPending = (scope: string, id: number) =>
    isActive(scope) && latestByKey.has(stateKey(scope, id));

  const getChain = (scope: string, id: number) =>
    chainByKey.get(stateKey(scope, id)) ?? Promise.resolve();

  const setChain = (scope: string, id: number, chain: Promise<void>) => {
    if (!isActive(scope)) return;
    chainByKey.set(stateKey(scope, id), chain);
  };

  const releaseChain = (scope: string, id: number, chain: Promise<void>) => {
    const key = stateKey(scope, id);
    if (chainByKey.get(key) !== chain) return;
    chainByKey.delete(key);
  };

  return {
    activateScope,
    begin,
    clearScope,
    complete,
    consumePending,
    getChain,
    getPending,
    hasPending,
    isActive,
    isCurrent,
    releaseChain,
    setChain,
    setPending,
  };
}

export type ScratchpadSaveState = ReturnType<typeof createScratchpadSaveState>;

export type ScratchpadSaveQueueOptions = {
  save: (id: number, notes: string) => Promise<ScratchpadSaveResult>;
  onSuccess: (id: number, notes: string, data: unknown) => void;
  onError: (
    id: number,
    notes: string,
    error: unknown,
    bestEffort: boolean,
  ) => void;
  scope?: string;
  state?: ScratchpadSaveState;
};

/**
 * Serializes scratchpad PATCHes per authorized scope and encounter.
 */
export function createScratchpadSaveQueue(options: ScratchpadSaveQueueOptions) {
  const scope = options.scope ?? DEFAULT_SCOPE;
  const state = options.state ?? createScratchpadSaveState();
  if (options.state === undefined) state.activateScope(scope);

  const enqueue = (
    id: number,
    notes: string,
    bestEffort = false,
  ): Promise<void> => {
    const saveGeneration = state.begin(scope, id);
    if (saveGeneration === undefined) return Promise.resolve();

    const previous = state.getChain(scope, id);
    const chain = previous
      .catch(() => {
        return undefined;
      })
      .then(async () => {
        if (!state.isCurrent(scope, id, saveGeneration)) return;
        try {
          const response = await options.save(id, notes);
          if (state.complete(scope, id, saveGeneration)) {
            options.onSuccess(id, notes, response.data);
          }
        } catch (error) {
          options.onError(id, notes, error, bestEffort);
        }
      });
    state.setChain(scope, id, chain);

    if (bestEffort) {
      state.setPending(scope, id, saveGeneration, {
        notes,
        settled: chain,
      });
    }

    void chain.then(() => {
      state.releaseChain(scope, id, chain);
    });
    return chain;
  };

  const retryPending = (
    id: number,
    pending: PendingScratchpadSave,
  ): Promise<void> | undefined => {
    if (!state.consumePending(scope, id, pending)) return undefined;
    return enqueue(id, pending.notes);
  };

  const hasPending = (id: number): boolean => state.hasPending(scope, id);

  return { enqueue, hasPending, retryPending };
}

export type ScratchpadSaveQueue = ReturnType<typeof createScratchpadSaveQueue>;
